import { useEffect, useState, type ReactNode } from "react";

import type { ClientId } from "../../domain/types";
import { TALLY_CONNECTION_KEY, type TallyConnectionSummary } from "../../domain/constants";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { tallyBooksApi, type TallyBankState } from "../../data/tallyBooksApi";
import { Callout } from "../../components/Callout";
import { SubStepShell } from "../../components/SubStepShell";
import { CoButton } from "../../components/Button";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

const TYPES = [
  { value: "bank", label: "Bank account" },
  { value: "credit", label: "Credit card" },
  { value: "overdraft", label: "Overdraft" },
];

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="font-normal text-muted-foreground/70"> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

export function TallyBanksStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const recorded = useCoAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY);
  const { goNext } = useSubStepNav(clientId, "banks-review");
  const [state, setState] = useState<TallyBankState | null>(null);
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("bank");
  const [accountNumber, setAccountNumber] = useState("");
  const [currencyCode, setCurrencyCode] = useState("INR");
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void tallyBooksApi.bankState(clientId).then(
      (next) => {
        if (!cancelled) setState(next);
      },
      (reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load bank accounts.");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!state?.pending.some((row) => row.status === "pending")) return undefined;
    const timer = window.setInterval(() => {
      void tallyBooksApi.bankState(clientId).then(setState).catch(() => undefined);
    }, 2000);

    return () => window.clearInterval(timer);
  }, [clientId, state]);

  useEffect(() => {
    if (!state || !client?.tallyCompanyName) return;
    const next: TallyConnectionSummary = {
      companyName: client.tallyCompanyName,
      gstin: client.tallyGstin.trim() || null,
      bankAccountCount: state.banks.length,
    };
    const same =
      recorded?.companyName === next.companyName &&
      (recorded?.gstin ?? null) === (next.gstin ?? null) &&
      recorded?.bankAccountCount === next.bankAccountCount;
    if (same) return;
    void persistAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY, next);
  }, [client?.tallyCompanyName, client?.tallyGstin, clientId, recorded, state]);

  async function add() {
    setBusy(true);
    setError(null);
    try {
      setState(
        await tallyBooksApi.createBank(clientId, {
          name: name.trim(),
          account_type: accountType,
          account_number: accountNumber.trim(),
          currency_code: currencyCode,
        })
      );
      setName("");
      setAccountNumber("");
      setFormOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create that account.");
    } finally {
      setBusy(false);
    }
  }

  const clientName = client?.name || "this client";

  return (
    <SubStepShell
      title="Banks & Credit Cards"
      description={`Accounts already in ${clientName}'s Tally company. A new account uses a currency from the previous step. INR is always available.`}
      thenWhat="Continue → Set Up Customers & Vendors."
      onContinue={() => void goNext()}
    >
      {error && !formOpen ? <Callout variant="warning">{error}</Callout> : null}
      {state === null ? (
        <p className="text-sm text-muted-foreground">Reading bank accounts…</p>
      ) : (
        <>
          {state.banks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bank, credit-card, or overdraft ledgers were read from Tally.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {state.banks.map((row) => (
                <li key={row.guid ?? row.name} className="flex items-baseline justify-between gap-3 rounded-md border px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium">{row.name}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {row.account_type}
                      {row.account_number ? ` · ${row.account_number}` : ""}
                    </span>
                  </span>
                  <span className="text-muted-foreground">{row.currency_code}</span>
                </li>
              ))}
            </ul>
          )}
          {state.pending.map((row) => (
            <p key={row.name} className={row.status === "failed" ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
              {row.status === "failed" ? row.error : `Creating ${row.name} in Tally…`}
            </p>
          ))}
          {formOpen ? (
            <div className="flex flex-col gap-3 rounded-lg border p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Account name">
                  <input
                    value={name}
                    maxLength={240}
                    placeholder="HDFC Current - 5432"
                    autoFocus
                    className={INPUT_CLASS}
                    onChange={(event) => setName(event.target.value)}
                  />
                </Field>
                <Field label="Type">
                  <select className={INPUT_CLASS} value={accountType} onChange={(event) => setAccountType(event.target.value)}>
                    {TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Account number" hint="optional">
                  <input
                    value={accountNumber}
                    maxLength={64}
                    className={cn(INPUT_CLASS, "font-mono")}
                    onChange={(event) => setAccountNumber(event.target.value)}
                  />
                </Field>
                <Field label="Currency" hint="enable more on the Currency step">
                  <select className={INPUT_CLASS} value={currencyCode} onChange={(event) => setCurrencyCode(event.target.value)}>
                    {state.currencies.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.code}
                        {item.code === "INR"
                          ? " — Indian Rupee (base)"
                          : item.name && item.name !== item.code
                            ? ` — ${item.name}`
                            : ""}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {error ? <Callout variant="warning">{error}</Callout> : null}
              <div className="flex items-center gap-2">
                <CoButton type="button" disabled={busy || name.trim() === ""} onClick={() => void add()}>
                  {busy ? "Adding…" : "Add to Tally"}
                </CoButton>
                <CoButton
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setFormOpen(false);
                    setError(null);
                  }}
                >
                  Cancel
                </CoButton>
              </div>
            </div>
          ) : (
            <div>
              <CoButton type="button" onClick={() => setFormOpen(true)}>
                Add bank account
              </CoButton>
            </div>
          )}
        </>
      )}
    </SubStepShell>
  );
}
